export type DocumentTaxCalculation = {
  status?: string;
  failureReason?: string | null;
  breakdownJson?: string | null;
  breakdown?: Array<{ name: string; rateBasisPoints: number; amountCents: number }>;
};

/** Labels come from the saved calculation, never from a guessed service zone. */
export function documentTaxRows(calculation?: DocumentTaxCalculation | null) {
  let raw: Array<{ amount?: number; jurisdiction?: { display_name?: string }; tax_rate_details?: { state?: string | null; country?: string; display_name?: string; percentage_decimal?: string | number } }> = [];
  try {
    const parsed: unknown = JSON.parse(calculation?.breakdownJson || "[]");
    if (Array.isArray(parsed)) raw = parsed.filter((row) => row && typeof row === "object");
  } catch { /* Historical documents may have no readable calculation snapshot. */ }
  return (calculation?.breakdown ?? []).map((tax) => {
    const matches = raw.filter((row) => row.amount === tax.amountCents &&
      row.tax_rate_details?.percentage_decimal != null &&
      Math.round(Number(row.tax_rate_details.percentage_decimal) * 100) === tax.rateBasisPoints);
    const names = new Set(matches.map((row) => {
      const details = row.tax_rate_details;
      const name = row.jurisdiction?.display_name?.trim() || details?.display_name?.trim() || "";
      return /^stripe tax$/i.test(name) ? "" : name;
    }).filter(Boolean));
    return { ...tax, name: names.size === 1 ? [...names][0] : "Sales tax" };
  });
}

export function documentTaxLabel(calculation?: DocumentTaxCalculation | null) {
  const names = [...new Set(documentTaxRows(calculation).map(row => row.name).filter(name => name !== "Sales tax"))];
  let rates: number[] = [];
  try {
    const raw: unknown = JSON.parse(calculation?.breakdownJson || "[]");
    if (Array.isArray(raw)) rates = [...new Set(raw.flatMap(row => {
      const value = row?.tax_rate_details?.percentage_decimal;
      const rate = value == null || value === "" ? NaN : Number(value);
      return Number.isFinite(rate) && rate >= 0 ? [rate] : [];
    }))];
  } catch { /* An unknown rate remains hidden; amounts never imply a rate. */ }
  const rateLabel = rates.map(rate => `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(rate)}%`).join(" / ");
  return `Sales tax${rateLabel ? ` (${rateLabel})` : ""}${names.length ? ` · ${names.join(" / ")}` : ""}`;
}
